import * as A from '@core/arena'

import { Nodes, Queue, Records } from '@openforis/arena-core'

import * as SurveyNodeDefs from '@core/survey/_survey/surveyNodeDefs'
import * as NodeDef from '@core/survey/nodeDef'
import * as NodeDefValidations from '@core/survey/nodeDefValidations'
import * as ObjectUtils from '@core/objectUtils'
import * as Node from '../node'
import { NodeValues } from '../nodeValues'

import { keys } from './recordKeys'
import { EntityKeysIndexCache } from './entityKeysIndexCache'

const {
  getChildren: getNodeChildren,
  getNodeByUuid,
  getNodesByDefUuid,
  getParent: getParentNode,
  getRoot: getRootNode,
} = Records

/**
 * === simple getters.
 */
export const getNodes = A.propOr({}, keys.nodes)
export const getNodesArray = (record) => Object.values(getNodes(record))

export { getNodeChildren, getNodeByUuid, getNodesByDefUuid, getRootNode, getParentNode }

export const findNodeChildren = (parentNode, childDefUuid) => (record) => {
  try {
    return getNodeChildren(parentNode, childDefUuid)(record)
  } catch (error) {
    return []
  }
}

// ==== hierarchy
// ancestors

export const visitAncestorsAndSelf = (node, visitor) => (record) =>
  // the visitor return value is ignored: the visit never stops early
  Records.visitAncestorsAndSelf(node, (visitedNode) => {
    visitor(visitedNode)
  })(record)

/**
 * Returns the list of ancestors from the given node to the root entity.
 * @param {object} node - The node to start from.
 * @returns {Function} - Function taking the record and returning the node and its ancestors.
 */
export const getAncestorsAndSelf = (node) => (record) => Records.getAncestorsAndSelf(node)(record)

export const getAncestorByNodeDefUuid = (node, ancestorDefUuid) => (record) =>
  A.pipe(
    getParentNode(node),
    (parentNode) => getAncestorsAndSelf(parentNode)(record),
    A.find((ancestor) => Node.getNodeDefUuid(ancestor) === ancestorDefUuid)
  )(record)

// Descendants
export const getNodeChildrenByDefUuidUnsorted = (parentNode, nodeDefUuid) => (record) =>
  getNodeChildren(parentNode, nodeDefUuid)(record)

export const getNodeChildrenByDefUuid = (parentNode, nodeDefUuid) => (record) => {
  const children = getNodeChildrenByDefUuidUnsorted(parentNode, nodeDefUuid)(record)
  return children.sort((node1, node2) => {
    if (Node.isPlaceholder(node1) && !Node.isPlaceholder(node2)) return 1
    if (!Node.isPlaceholder(node1) && Node.isPlaceholder(node2)) return -1
    if (node1.id < node2.id || node1.dateCreated < node2.dateCreated) return -1
    if (node1.id > node2.id || node1.dateCreated > node2.dateCreated) return 1
    return 0
  })
}

export const getNodeChildByDefUuid = (parentNode, nodeDefUuid) =>
  A.pipe(getNodeChildrenByDefUuid(parentNode, nodeDefUuid), A.head)

export const getNodeChildIndex = (node) => (record) => {
  const parentNode = getParentNode(node)(record)
  const siblings = getNodeChildrenByDefUuid(parentNode, Node.getNodeDefUuid(node))(record)
  return siblings.findIndex((sibling) => Node.isEqual(sibling)(node))
}

export const visitDescendantsAndSelf =
  (node, visitor, stopIfFn = null) =>
  (record) => {
    const queue = new Queue()

    queue.enqueue(node)

    while (!queue.isEmpty()) {
      const node = queue.dequeue()

      visitor(node)

      if (stopIfFn?.(node)) {
        break
      }
      const children = getNodeChildren(node)(record)
      queue.enqueueItems(children)
    }
  }

export const findDescendantOrSelf = (node, filterFn) => (record) => {
  let found = null
  visitDescendantsAndSelf(
    node,
    (currentNode) => {
      if (filterFn(currentNode)) found = currentNode
    },
    () => found !== null
  )(record)
  return found
}

/**
 * Finds a the parent node of the specified node def, starting from the specified parent node and traversing
 * the single entities, if any, down to the correct parent node.
 * @param root0
 * @param root0.survey
 * @param root0.parentNode
 * @param root0.nodeDefUuid
 */
export const getNodeParentInDescendantSingleEntities =
  ({ survey, parentNode, nodeDefUuid }) =>
  (record) => {
    const nodeDefParent = SurveyNodeDefs.getNodeDefByUuid(Node.getNodeDefUuid(parentNode))(survey)
    const nodeDef = SurveyNodeDefs.getNodeDefByUuid(nodeDefUuid)(survey)
    if (!NodeDef.isDescendantOf(nodeDefParent)(nodeDef)) {
      throw new Error(`target node is not a descendant of the specified parent entity:\
        parentNodeDef: ${NodeDef.getName(nodeDefParent)} descendantDef: ${NodeDef.getName(nodeDef)}`)
    }
    const nodeDefHierarchy = NodeDef.getMetaHierarchy(nodeDef)
    const nodeDefParentHierarchy = NodeDef.getMetaHierarchy(nodeDefParent)
    const hierarchyToVisit =
      nodeDefHierarchy.length > nodeDefParentHierarchy.length + 1
        ? nodeDefHierarchy.slice(nodeDefParentHierarchy.length + 1)
        : []

    let currentParentNode = parentNode

    for (const descendantDefUuid of hierarchyToVisit) {
      const nodeDefDescendant = SurveyNodeDefs.getNodeDefByUuid(descendantDefUuid)(survey)
      if (NodeDef.isSingleEntity(nodeDefDescendant)) {
        currentParentNode = getNodeChildByDefUuid(currentParentNode, descendantDefUuid)(record)
      } else {
        throw new Error(
          `the target node ${NodeDef.getName(nodeDef)} is inside a multiple entity: ${NodeDef.getName(
            nodeDefDescendant
          )}`
        )
      }
    }
    return currentParentNode
  }

/**
 * Returns true if a node and all its ancestors are applicable.
 * @param node
 */
export const isNodeApplicable = (node) => (record) => {
  if (Node.isRoot(node)) {
    return true
  }

  const nodeParent = getParentNode(node)(record)
  const isApplicable = Node.isChildApplicable(Node.getNodeDefUuid(node))(nodeParent)
  if (isApplicable) {
    return isNodeApplicable(nodeParent)(record)
  }

  return false
}

// ==== dependency
/**
 * Returns a list of dependent node pointers.
 * Every item in the list is in the format:
 * {
 *   nodeCtx, //context node
 *   nodeDef, //node definition
 * }.
 * @param survey
 * @param node
 * @param dependencyType
 * @param includeSelf
 * @param filterFn
 */
export const getDependentNodePointers =
  (survey, node, dependencyType, includeSelf = false, filterFn = null) =>
  (record) =>
    Records.getDependentNodePointers(survey, node, dependencyType, includeSelf, filterFn)(record)

// Code attributes
export const getDependentCodeAttributes = (node) => (record) => Records.getDependentCodeAttributes(node)(record)

export const getParentCodeAttribute = (_survey, parentNode, nodeDef) => (record) =>
  Records.getParentCodeAttribute(parentNode, nodeDef)(record)

// Visits the ancestor code attributes in bottom-up order
export const visitAncestorCodeAttributes =
  ({ survey, parentNode, nodeDef, visitor }) =>
  (record) => {
    const visitedNodeUuids = new Set() // avoid cycles
    let currentParentCodeAttribute = Records.getParentCodeAttribute(parentNode, nodeDef)(record)
    while (currentParentCodeAttribute && !visitedNodeUuids.has(Node.getUuid(currentParentCodeAttribute))) {
      visitedNodeUuids.add(Node.getUuid(currentParentCodeAttribute))
      visitor(currentParentCodeAttribute)
      const parentCodeAttributeNodeDef = SurveyNodeDefs.getNodeDefByUuid(
        Node.getNodeDefUuid(currentParentCodeAttribute)
      )(survey)
      const ancestorNode = Records.getParent(currentParentCodeAttribute)(record)
      currentParentCodeAttribute = Records.getParentCodeAttribute(ancestorNode, parentCodeAttributeNodeDef)(record)
    }
  }

// Returns the ancestor code attributes, in top-down order
export const getAncestorCodeAttributes =
  ({ survey, parentNode, nodeDef }) =>
  (record) => {
    const result = []
    visitAncestorCodeAttributes({
      survey,
      parentNode,
      nodeDef,
      visitor: (visitedCodeAttribute) => {
        result.unshift(visitedCodeAttribute)
      },
    })(record)
    return result
  }

// ====== Keys

export const getEntityKeyNodes = (survey, nodeEntity) => (record) =>
  Records.getEntityKeyNodes(survey, nodeEntity)(record)

export const getEntityKeyValues = (survey, nodeEntity) =>
  A.pipe(getEntityKeyNodes(survey, nodeEntity), A.map(Node.getValue))

export const findChildByKeyValues =
  ({ survey, parentNode, childDefUuid, keyValuesByDefUuid, entityKeysIndexCache = null }) =>
  (record) => {
    const childDef = SurveyNodeDefs.getNodeDefByUuid(childDefUuid)(survey)
    const siblings = getNodeChildrenByDefUuidUnsorted(parentNode, childDefUuid)(record)
    if (NodeDef.isSingleEntity(childDef)) {
      return siblings[0]
    }
    // key defs are computed once (and not for every sibling): siblings can be many (e.g. when importing data)
    const applicableKeyDefs = SurveyNodeDefs.getNodeDefKeys(childDef)(survey).filter((keyDef) =>
      Nodes.isChildApplicable(parentNode, NodeDef.getUuid(keyDef))
    )
    const hasKeyValues = (entity) =>
      applicableKeyDefs.every((keyDef) => {
        const keyDefUuid = NodeDef.getUuid(keyDef)
        const keyAttribute = getNodeChildByDefUuid(entity, keyDefUuid)(record)
        const keyAttributeValue = Node.getValue(keyAttribute)
        const keyAttributeValueSearch = keyValuesByDefUuid[keyDefUuid]

        return NodeValues.isValueEqual({
          survey,
          nodeDef: keyDef,
          record,
          parentNode: entity,
          attribute: keyAttribute,
          value: keyAttributeValue,
          valueSearch: keyAttributeValueSearch,
        })
      })

    if (entityKeysIndexCache && EntityKeysIndexCache.canBeUsed(applicableKeyDefs)) {
      const entityUuids = entityKeysIndexCache.findEntityUuids({
        parentNode,
        childDefUuid,
        keyDefs: applicableKeyDefs,
        siblings,
        getKeyAttribute: (entity, keyDefUuid) => getNodeChildByDefUuid(entity, keyDefUuid)(record),
        keyValuesByDefUuid,
      })
      if (entityUuids) {
        // entities found in the index are checked again: key values could have been modified after the index has been built
        return entityUuids
          .map((entityUuid) => getNodeByUuid(entityUuid)(record))
          .find((entity) => entity && !Node.isDeleted(entity) && hasKeyValues(entity))
      }
    }
    return siblings.find(hasKeyValues)
  }

export const findDescendantByKeyValues =
  ({ survey, descendantDefUuid, keyValuesByDefUuid, entityKeysIndexCache = null }) =>
  (record) => {
    // start from root node
    const rootNode = getRootNode(record)
    if (NodeDef.getUuid(SurveyNodeDefs.getNodeDefRoot(survey)) === descendantDefUuid) {
      // the descendant is the root entity
      return rootNode
    }
    let currentNode = rootNode
    // visit descendant nodes up to descendant def (excluding root entity)
    const entityDef = SurveyNodeDefs.getNodeDefByUuid(descendantDefUuid)(survey)
    const hierarchyToVisit = [...NodeDef.getMetaHierarchy(entityDef), descendantDefUuid]
    hierarchyToVisit.shift()

    while (hierarchyToVisit.length && currentNode) {
      const nodeDefUuid = hierarchyToVisit.shift()
      const descendant = findChildByKeyValues({
        survey,
        parentNode: currentNode,
        childDefUuid: nodeDefUuid,
        keyValuesByDefUuid,
        entityKeysIndexCache,
      })(record)

      currentNode = descendant
    }
    return currentNode
  }

// ===== Unique

export const getAttributesUniqueSibling = ({ record, attribute, attributeDef }) => {
  const parentEntity = getParentNode(attribute)(record)
  const ancestorEntity = getParentNode(parentEntity)(record)
  const siblingParentEntities = getNodeChildrenByDefUuid(ancestorEntity, NodeDef.getParentUuid(attributeDef))(record)

  return siblingParentEntities.reduce(
    (siblingsAcc, siblingEntity) => [
      ...siblingsAcc,
      ...getNodeChildrenByDefUuid(siblingEntity, NodeDef.getUuid(attributeDef))(record),
    ],
    []
  )
}

const _isNodeDefUnique = (nodeDef) => NodeDefValidations.isUnique(NodeDef.getValidations(nodeDef))

export const getAttributesUniqueDependent = ({ survey, record, node }) => {
  const nodeDef = SurveyNodeDefs.getNodeDefByUuid(Node.getNodeDefUuid(node))(survey)
  if (NodeDef.isSingleEntity(nodeDef)) return {}

  let siblingUniqueAttributes = []
  if (NodeDef.isEntity(nodeDef)) {
    const nodeDefsUnique = SurveyNodeDefs.getNodeDefChildren({ nodeDef })(survey).filter(_isNodeDefUnique)

    siblingUniqueAttributes = nodeDefsUnique.reduce((siblingsAcc, nodeDefUnique) => {
      if (NodeDef.isSingle(nodeDefUnique)) {
        const attribute = getNodeChildByDefUuid(node, NodeDef.getUuid(nodeDefUnique))(record)
        siblingsAcc.push(...getAttributesUniqueSibling({ record, attribute, attributeDef: nodeDefUnique }))
      }
      return siblingsAcc
    }, [])
  } else if (_isNodeDefUnique(nodeDef)) {
    siblingUniqueAttributes = getAttributesUniqueSibling({ record, attribute: node, attributeDef: nodeDef })
  }
  return ObjectUtils.toUuidIndexedObj(siblingUniqueAttributes)
}

export const isNodeFilledByUser = (node) => (record) => Records.isNodeFilledByUser(node)(record)

export const isNodeEmpty = (node) => (record) => Records.isNodeEmpty(node)(record)

export const isEmpty = (record) => Records.isEmpty(record)
